// Máscaras de campo do sistema todo (lista B de 30/10, item 4): a máscara se forma enquanto digita, só os dígitos contam (parênteses,
// ponto, barra, hífen e espaço digitados são ignorados) e colar com ou sem formato dá o mesmo resultado. Funções puras: o componente
// `EntradaComMascara` (componentes/formulario.tsx) e o gancho `useMascaraDigitando` (componentes/cursor-mascara.ts) só as aplicam.
// O que se grava segue o contrato de cada campo (documento, CEP e telefone com a máscara, que o servidor normaliza; dinheiro em
// centavos; percentual em número); esta camada não muda o contrato da API.
import { formatarDigitandoDocumentoPais } from '@malgoos/shared';
import { mascarar } from './campo-data';
import { deCentavos, mascaraTelefone, mascaraTelefoneDigitando } from './formato';

/** Texto digitado (ou colado) no texto com a máscara. `anterior` é o que o campo mostrava antes da edição. */
export type FormatoMascara = (bruto: string, anterior?: string) => string;

export { mascaraTelefone, mascaraTelefoneDigitando };

/** Só os dígitos de um texto. */
export const soDigitos = (v: string) => v.replace(/\D/g, '');

/** Telefone e WhatsApp: "(11) 99999-9999" (celular, 11 dígitos) ou "(11) 3333-3333" (fixo, 10); com "+" ou DDI, fica como a regra do núcleo. */
export const mascaraTelefoneBR: FormatoMascara = (v) => mascaraTelefoneDigitando(v);

/** CPF: 000.000.000-00. */
export const mascaraCpf: FormatoMascara = (v) => {
  const d = soDigitos(v).slice(0, 11);
  let r = d.slice(0, 3);
  if (d.length > 3) r += `.${d.slice(3, 6)}`;
  if (d.length > 6) r += `.${d.slice(6, 9)}`;
  if (d.length > 9) r += `-${d.slice(9)}`;
  return r;
};

/** CNPJ: 00.000.000/0000-00. */
export const mascaraCnpj: FormatoMascara = (v) => {
  const d = soDigitos(v).slice(0, 14);
  let r = d.slice(0, 2);
  if (d.length > 2) r += `.${d.slice(2, 5)}`;
  if (d.length > 5) r += `.${d.slice(5, 8)}`;
  if (d.length > 8) r += `/${d.slice(8, 12)}`;
  if (d.length > 12) r += `-${d.slice(12)}`;
  return r;
};

/** "CPF ou CNPJ": até 11 dígitos é CPF, mais que isso é CNPJ. */
export const mascaraCpfCnpj: FormatoMascara = (v) => (soDigitos(v).length <= 11 ? mascaraCpf(v) : mascaraCnpj(v));

/** CEP: 00000-000. */
export const mascaraCepBR: FormatoMascara = (v) => {
  const d = soDigitos(v).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

/** Data em campo de texto: dd/mm/aaaa (a ordem do idioma é do `CampoData`; aqui só a forma dos dígitos). */
export const mascaraDataTexto: FormatoMascara = (v) => mascarar('date', v);

/** Hora em campo de texto: hh:mm. */
export const mascaraHoraTexto: FormatoMascara = (v) => mascarar('time', v);

/**
 * Documento do campo (`DefCampo.documento`): `cpf`, `cnpj`, `qualquer` (CPF ou CNPJ pelo tamanho) ou o de outro país (EIN, NIF...).
 * Era `mascaraDocumento` em `componentes/formulario.tsx`, que a reexporta.
 */
export function mascaraDocumentoDo(v: string, qual: string = 'qualquer'): string {
  if (qual === 'cpf') return mascaraCpf(v);
  if (qual === 'cnpj') return mascaraCnpj(v);
  if (qual === 'qualquer') return mascaraCpfCnpj(v);
  return formatarDigitandoDocumentoPais(qual as Parameters<typeof formatarDigitandoDocumentoPais>[0], v);
}

/** O zero que sobrou de apagar dígitos (0,0x depois de apagar) esvazia o campo; o zero digitado no campo vazio fica (0,00). */
const apagou = (digitos: string, anterior?: string) => !!anterior && digitos.length < soDigitos(anterior).length;

/** Máximo de dígitos do dinheiro e do percentual (cabe em número seguro com 2 casas). */
const MAX_DIGITOS_VALOR = 15;

/**
 * Dinheiro enquanto digita, "1.234,56" no idioma: só os dígitos contam e entram da direita (R$ 0,05 depois 0,50 depois 5,00), como
 * no caixa. Colar "R$ 1.234,56" ou "123456" dá o mesmo. O sinal "-" digitado ou colado deixa o valor negativo. Apagar um dígito
 * até sobrar zero esvazia o campo. O símbolo "R$" fica no exemplo (placeholder), não no texto, que `paraCentavos` lê de volta.
 */
export const mascaraDinheiro: FormatoMascara = (bruto, anterior) => {
  const d = soDigitos(bruto);
  const negativo = bruto.includes('-');
  if (!d) return negativo ? '-' : '';
  const c = Number(d.slice(0, MAX_DIGITOS_VALOR));
  if (c === 0 && apagou(d, anterior)) return '';
  return `${negativo && c > 0 ? '-' : ''}${deCentavos(c)}`;
};

/** Texto do dinheiro em centavos (ou null com o campo vazio). */
export function centavosDoTexto(texto: string): number | null {
  const d = soDigitos(texto);
  if (!d) return null;
  const c = Number(d.slice(0, MAX_DIGITOS_VALOR));
  return texto.includes('-') && c > 0 ? -c : c;
}

const SUFIXO_PERCENTUAL = /\s?%$/;

/**
 * Percentual enquanto digita, "12,50 %": como o dinheiro (dígitos entram da direita, duas casas) e com o " %" no fim. Apagar o
 * "%" ou o espaço com o cursor no fim conta como apagar o último dígito, para o sufixo não travar o apagar.
 */
export const mascaraPercentual: FormatoMascara = (bruto, anterior) => {
  let d = soDigitos(bruto);
  const apagouSufixo = !!anterior && SUFIXO_PERCENTUAL.test(anterior) && !bruto.includes('%') && d === soDigitos(anterior);
  if (apagouSufixo) d = d.slice(0, -1);
  if (!d) return '';
  const c = Number(d.slice(0, MAX_DIGITOS_VALOR));
  if (c === 0 && apagou(d, anterior)) return '';
  return `${deCentavos(c)} %`;
};

/** Percentual como número (12,5), ou null com o campo vazio. */
export function percentualDoTexto(texto: string): number | null {
  const d = soDigitos(texto);
  if (!d) return null;
  return Number(d.slice(0, MAX_DIGITOS_VALOR)) / 100;
}

/** Número (12.5) no texto do percentual ("12,50 %"). */
export function textoDoPercentual(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  return `${deCentavos(Math.round(Math.abs(n) * 100))} %`;
}

/** Lista de 04/11, item 3: o exemplo do campo de dinheiro numa moeda dada (BRL é o `EXEMPLOS_MASCARA.dinheiro` de sempre). */
export const exemploDinheiroEm = (simbolo: string) => `${simbolo} ${deCentavos(0)}`;

/** Exemplos de cada formato para o placeholder (o dinheiro e o percentual seguem o separador do idioma). */
export const EXEMPLOS_MASCARA = {
  telefone: '(11) 99999-9999',
  telefoneFixo: '(11) 3333-3333',
  cpf: '000.000.000-00',
  cnpj: '00.000.000/0000-00',
  cpfCnpj: '000.000.000-00',
  cep: '00000-000',
  data: 'dd/mm/aaaa',
  hora: 'hh:mm',
  dinheiro: () => `R$ ${deCentavos(0)}`,
  percentual: () => `${deCentavos(0)} %`,
} as const;

/** De que lado do cursor se contam os caracteres que contam: documento, telefone e CEP pela esquerda; valores pela direita. */
export type LadoCursor = 'esquerda' | 'direita';

const SIGNIFICATIVO_ESQ = /[\p{L}\p{N}+]/u;
const SIGNIFICATIVO_DIR = /\d/;

/**
 * Aplica a máscara ao texto digitado e devolve onde o cursor deve ficar. O cursor segue os caracteres que contam (dígitos; letras e
 * "+" nos documentos e telefones de fora), não a posição no texto: digitar no meio não pula o cursor para o fim e apagar um
 * separador só anda o cursor sobre ele.
 */
export function aplicarMascara(bruto: string, cursor: number, formato: FormatoMascara, opcoes: { lado?: LadoCursor; anterior?: string } = {}): { texto: string; cursor: number } {
  const texto = formato(bruto, opcoes.anterior);
  const lado = opcoes.lado ?? 'esquerda';
  const sig = lado === 'esquerda' ? SIGNIFICATIVO_ESQ : SIGNIFICATIVO_DIR;
  const chars = [...texto];
  const idx = chars.map((c, i) => (sig.test(c) ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return { texto, cursor: lado === 'esquerda' ? 0 : texto.length };
  const comp = (s: string) => [...s].filter((c) => sig.test(c)).length;
  const emUnidades = (n: number) => chars.slice(0, n).join('').length;
  if (lado === 'esquerda') {
    const n = comp(bruto.slice(0, cursor));
    if (n === 0) return { texto, cursor: emUnidades(idx[0]!) };
    const i = idx[Math.min(n, idx.length) - 1]!;
    return { texto, cursor: emUnidades(i + 1) };
  }
  const n = comp(bruto.slice(cursor));
  if (n >= idx.length) return { texto, cursor: emUnidades(idx[0]!) };
  // `n` dígitos depois do cursor: o cursor fica logo depois do dígito de ordem (total - n).
  const i = idx[idx.length - n - 1]!;
  return { texto, cursor: emUnidades(i + 1) };
}
